import React from 'react';
import {Box, CircularProgress, IconButton, InputAdornment, TextField, Typography} from '@mui/material';
import {Clear, Search} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';

interface SemanticSearchBarProps {
    value: string;
    onChange: (value: string) => void;
    loading: boolean;
    unavailable: boolean;
    error: boolean;
    /** number of matches after filtering; shown when query is non-empty and not loading */
    matchCount?: number;
    placeholder: string;
}

/**
 * Compact search input with a debounced semantic-query workflow.
 * Visual states:
 *   - idle (empty): plain input, search icon
 *   - loading: spinner in adornment, status hint "suche..."
 *   - unavailable (503): muted message under input
 *   - results: "X Treffer" / "Keine Treffer"
 */
export const SemanticSearchBar: React.FC<SemanticSearchBarProps> = ({
    value,
    onChange,
    loading,
    unavailable,
    error,
    matchCount,
    placeholder,
}) => {
    const {t} = useTranslation();
    const hasQuery = value.trim().length > 0;

    let statusLine: string | null = null;
    if (hasQuery) {
        if (unavailable) statusLine = t('common.searchUnavailable');
        else if (error) statusLine = t('common.searchError');
        else if (!loading && matchCount != null) {
            statusLine = matchCount === 0
                ? t('common.searchNoResults')
                : t('common.searchResults', {count: matchCount});
        }
    }

    return (
        <Box sx={{width: '100%'}}>
            <TextField
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={placeholder}
                size="small"
                fullWidth
                aria-label={t('common.search')}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                {loading
                                    ? <CircularProgress size={18}/>
                                    : <Search fontSize="small" color="action"/>}
                            </InputAdornment>
                        ),
                        endAdornment: hasQuery ? (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={() => onChange('')}
                                    aria-label={t('common.searchClear')}
                                    title={t('common.searchClear')}
                                >
                                    <Clear fontSize="small"/>
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    },
                }}
            />
            {statusLine && (
                <Typography
                    variant="caption"
                    color={unavailable || error ? 'warning.main' : 'text.secondary'}
                    sx={{display: 'block', mt: 0.5, ml: 1.5}}
                >
                    {statusLine}
                </Typography>
            )}
        </Box>
    );
};
